import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageSquare, RefreshCw } from "lucide-react";

function formatDate(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString("pt-BR");
}

function score(value) {
  return `${value ?? "—"}/5`;
}

export default function AdminFeedback() {
  const [athletes, setAthletes] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState("__all__");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data: athleteData }, { data: feedbackData }] = await Promise.all([
        api.get("/athletes"),
        api.get("/feedback"),
      ]);
      setAthletes(athleteData);
      setFeedbacks(feedbackData);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Não foi possível carregar os feedbacks.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const athleteById = useMemo(
    () => Object.fromEntries(athletes.map((athlete) => [athlete.id, athlete])),
    [athletes],
  );

  const filteredFeedbacks = useMemo(() => {
    const query = search.trim().toLowerCase();
    return feedbacks.filter((feedback) => {
      if (selectedAthlete !== "__all__" && feedback.athlete_id !== selectedAthlete) return false;
      if (!query) return true;
      const athlete = athleteById[feedback.athlete_id];
      return [athlete?.name, athlete?.email, feedback.week_start, feedback.notes]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [athleteById, feedbacks, search, selectedAthlete]);

  return (
    <AppLayout kind="admin">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-semibold text-white sm:text-4xl">
            <MessageSquare className="h-8 w-8 text-amber-400" />
            Feedbacks dos atletas
          </h1>
          <p className="mt-1 text-slate-400">Consulte todos os feedbacks autorizados, sem limitar ao histórico recente.</p>
        </div>
        <Button type="button" variant="outline" onClick={load} disabled={loading}
          className="border-amber-500/30 bg-transparent text-slate-100 hover:bg-amber-500/10" data-testid="feedback-refresh">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      <Card className="mb-6 bg-transparent p-5 gsx-card-dark">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
          <div className="space-y-2">
            <Label htmlFor="feedback-athlete" className="text-slate-300">Atleta</Label>
            <select id="feedback-athlete" value={selectedAthlete} onChange={(event) => setSelectedAthlete(event.target.value)}
              data-testid="feedback-athlete-filter"
              className="h-10 w-full rounded-md border border-amber-500/20 bg-[#0D2A52] px-3 text-sm text-white outline-none focus:border-amber-400">
              <option value="__all__">Todos os atletas</option>
              {athletes.map((athlete) => <option key={athlete.id} value={athlete.id}>{athlete.name}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="feedback-search" className="text-slate-300">Buscar</Label>
            <Input id="feedback-search" value={search} onChange={(event) => setSearch(event.target.value)}
              placeholder="Nome, e-mail, semana ou observação" data-testid="feedback-search"
              className="border-amber-500/20 bg-[#0D2A52] text-white placeholder:text-slate-500" />
          </div>
          <div className="text-sm text-slate-400">
            Exibindo <span className="font-mono text-amber-300">{filteredFeedbacks.length}</span> de <span className="font-mono text-amber-300">{feedbacks.length}</span>
          </div>
        </div>
      </Card>

      {loading && <div className="py-12 text-center text-slate-400">Carregando feedbacks…</div>}
      {!loading && error && (
        <Card className="bg-transparent p-6 gsx-card-dark">
          <div role="alert" className="text-sm text-red-200">{error}</div>
          <Button type="button" variant="outline" onClick={load} className="mt-4 border-red-300/30 text-red-100 hover:bg-red-400/10">Tentar novamente</Button>
        </Card>
      )}
      {!loading && !error && !filteredFeedbacks.length && (
        <Card className="bg-transparent p-8 text-center text-slate-400 gsx-card-dark">Nenhum feedback encontrado.</Card>
      )}
      {!loading && !error && filteredFeedbacks.length > 0 && (
        <div className="grid gap-4 xl:grid-cols-2">
          {filteredFeedbacks.map((feedback) => {
            const athlete = athleteById[feedback.athlete_id];
            return (
              <Card key={feedback.id} className="bg-transparent p-5 gsx-card-dark" data-testid={`feedback-card-${feedback.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2 border-b border-white/10 pb-3">
                  <div>
                    <h2 className="font-semibold text-white">{athlete?.name || "Atleta não encontrado"}</h2>
                    <p className="text-xs text-slate-400">{athlete?.email || feedback.athlete_id}</p>
                  </div>
                  <div className="text-right text-xs text-amber-300">
                    <div>{formatDate(feedback.created_at)}</div>
                    <div className="text-slate-500">Semana de {feedback.week_start || "—"}</div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                  <Metric label="Horas" value={feedback.hours_trained} />
                  <Metric label="Plano" value={score(feedback.plan_completion)} />
                  <Metric label="Motivação" value={score(feedback.motivation)} />
                  <Metric label="Concentração" value={score(feedback.concentration)} />
                  <Metric label="Treinos" value={score(feedback.training_performance)} />
                  <Metric label="Partidas" value={score(feedback.match_performance)} />
                </div>

                <div className="mt-4 grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
                  <FeedbackText label="Dificuldades" value={feedback.difficulties} />
                  <FeedbackText label="Facilidades" value={feedback.facilities} />
                  <FeedbackText label="A melhorar" value={feedback.to_improve} />
                  <FeedbackText label="Próxima meta" value={feedback.next_goal} />
                  <FeedbackText label="Observações" value={feedback.notes} wide />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}

function Metric({ label, value }) {
  return <div className="rounded-md border border-white/10 bg-[#0D2A52]/50 p-3"><div className="text-xs text-slate-500">{label}</div><div className="mt-1 font-mono text-amber-300">{value}</div></div>;
}

function FeedbackText({ label, value, wide = false }) {
  return <div className={wide ? "sm:col-span-2" : ""}><div className="text-xs text-slate-500">{label}</div><p className="mt-1 whitespace-pre-wrap">{value || "—"}</p></div>;
}
